import { ColumnSizingState, Updater } from '@tanstack/react-table'
import { useCallback, useRef, useState } from 'react'

/**
 * Persist user-adjusted column widths to localStorage, namespaced per user +
 * table — mirroring how ColumnsStore persists column visibility/order
 * (`${userId}-columns-${tableKey}`). Widths are intentionally device-local
 * (localStorage, not the server) because a comfortable width on a desktop is
 * wrong on a tablet or phone; layout should follow the viewport, not the account.
 *
 * Returns TanStack-compatible `[columnSizing, onColumnSizingChange]`. Floors are
 * enforced separately via each column's `minSize` (TanStack's `getSize()` clamps
 * to it), so a too-small persisted value can never render below its floor even
 * if it lingers in storage from before a floor changed.
 */
export function useColumnWidthPersistence({
  tableKey,
  userId,
  enabled = true,
}: {
  tableKey: string
  userId?: number | string
  enabled?: boolean
}): [ColumnSizingState, (updater: Updater<ColumnSizingState>) => void] {
  const storageKey = `${userId ?? 'anon'}-colwidths-${tableKey}`
  const storageKeyRef = useRef(storageKey)
  storageKeyRef.current = storageKey

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() => {
    if (!enabled || typeof window === 'undefined') return {}
    try {
      const raw = window.localStorage.getItem(storageKey)
      const parsed = raw ? JSON.parse(raw) : null
      // Guard against malformed/legacy values; only keep numeric widths.
      if (parsed && typeof parsed === 'object') {
        return Object.fromEntries(
          Object.entries(parsed).filter(
            ([, v]) => typeof v === 'number' && Number.isFinite(v)
          )
        ) as ColumnSizingState
      }
    } catch {
      // Corrupt entry — fall back to defaults rather than crashing the table.
    }
    return {}
  })

  // Latest widths + a debounce timer. columnResizeMode 'onChange' fires this
  // handler on every mousemove pixel during a drag (60+/sec); the in-memory
  // state update must stay immediate (smooth resize), but the synchronous
  // JSON.stringify + localStorage.setItem only needs to land once the drag
  // settles. So we update state synchronously and debounce the persist.
  const latestSizingRef = useRef<ColumnSizingState>(columnSizing)
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onColumnSizingChange = useCallback(
    (updater: Updater<ColumnSizingState>) => {
      setColumnSizing((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (old: ColumnSizingState) => ColumnSizingState)(prev)
            : updater
        latestSizingRef.current = next
        return next
      })

      if (!enabled || typeof window === 'undefined') return

      if (persistTimerRef.current) clearTimeout(persistTimerRef.current)
      persistTimerRef.current = setTimeout(() => {
        try {
          window.localStorage.setItem(
            storageKeyRef.current,
            JSON.stringify(latestSizingRef.current)
          )
        } catch {
          // Storage full / disabled — keep the in-memory value, skip persist.
        }
      }, 200)
    },
    [enabled]
  )

  return [columnSizing, onColumnSizingChange]
}
